import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Auth, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut,
         sendPasswordResetEmail, sendEmailVerification, UserCredential,
         onAuthStateChanged, User as FirebaseUser, updateProfile,
         verifyPasswordResetCode, confirmPasswordReset } from '@angular/fire/auth';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { AuthApiService } from '../api-services/auth-api.service';
import { AuthUser } from '../data-models/auth-user.model';
import { LoginFailedResponse } from '../response-models/login-failed-response.model';
import { UserRoleHelper } from '../utils/user-role-utils';
import { GlobalRoles } from '../data-models/global-roles.model';
import { CustomClaims } from '../data-models/custom-claims.model';
import { CustomClaimsData } from '../data-models/custom-claims-data.model';
import { ClaimMembershipData } from '../data-models/claim-membership-data.model';
import { firebaseErrorMessage } from '../../shared/utils/firebase-error.utils';

/**
 * Authentication Service
 * Manages Firebase authentication and custom claims
 * Based on: agent-os/specs/01-user-authentication-sso/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly authApi = inject(AuthApiService);
  private currentUserSubject = new BehaviorSubject<AuthUser | null>(null);
  public currentUser$: Observable<AuthUser | null> = this.currentUserSubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(true);
  public loading$: Observable<boolean> = this.loadingSubject.asObservable();

  constructor(
    private auth: Auth,
    private router: Router
  ) {
    this.initAuthListener();
  }

  /**
   * Record a completed login on the backend.
   *
   * Admin auth happens client-side via Firebase, so the Hub never sees a login. This pings
   * POST /auth/record-login after sign-in so the Hub can stamp lastLogin. The auth interceptor sends
   * `X-Client-Platform: web`, so the login audit is attributed to the web platform.
   * Best-effort — failures must not block sign-in.
   */
  private recordLogin(): void {
    void firstValueFrom(this.authApi.recordLogin()).catch(err => console.error('Failed to record login:', err));
  }

  /** POST /auth/login-failed. Resolves `undefined` when the call itself fails (best-effort). */
  async recordFailedLoginAttempt(email: string): Promise<LoginFailedResponse | void> {
    return firstValueFrom(this.authApi.recordFailedLogin(email)).catch(err => console.error('Failed to record failed login attempt:', err));
  }

  /**
   * Initialize Firebase auth state listener
   */
  private initAuthListener(): void {
    onAuthStateChanged(this.auth, async (firebaseUser) => {
      if (firebaseUser) {
        const user = await this.mapFirebaseUserToUser(firebaseUser);
        this.currentUserSubject.next(user);
      } else {
        this.currentUserSubject.next(null);
      }
      this.loadingSubject.next(false);
    });
  }

  /**
   * Map Firebase user to ADN user model with custom claims
   * Parses compressed custom claims format: {c: <globalClaims>, m: [memberships]}
   */
  private async mapFirebaseUserToUser(firebaseUser: FirebaseUser): Promise<AuthUser> {
    const idTokenResult = await firebaseUser.getIdTokenResult();

    // Parse compressed custom claims format from Firebase token
    // Backend sets claims as: {c: <globalClaims>, m: [{o: <orgId>, c: <orgClaims>}]}
    const compressedClaims: CustomClaimsData = {
      c: idTokenResult.claims['c'] as number | undefined,
      m: idTokenResult.claims['m'] as ClaimMembershipData[] | undefined
    };

    // Convert to CustomClaims object for easier handling
    const customClaims = compressedClaims.c !== undefined || compressedClaims.m !== undefined
      ? CustomClaims.fromJSON(compressedClaims)
      : undefined;

    const user: AuthUser = {
      uid: firebaseUser.uid,
      email: firebaseUser.email!,
      displayName: firebaseUser.displayName ?? undefined,
      photoURL: firebaseUser.photoURL ?? undefined,
      emailVerified: firebaseUser.emailVerified,
      customClaims: customClaims
    };

    return user;
  }

  /**
   * Sign in with email and password
   */
  async signIn(email: string, password: string): Promise<AuthUser> {
    try {
      const credential: UserCredential = await signInWithEmailAndPassword(this.auth, email, password);
      const user = await this.mapFirebaseUserToUser(credential.user);

      // Check if email is verified
      if (!user.emailVerified) {
        throw new Error('Please verify your email before signing in.');
      }

      // Record the login on the backend (best-effort — must not block sign-in)
      this.recordLogin();

      return user;
    } catch (error: any) {
      const wrong_password_codes = ['auth/invalid-credential', 'auth/invalid-password']
      if(wrong_password_codes.includes(error.code)) {
        this.recordFailedLoginAttempt(email).catch(err => console.error('Failed to record failed login:', err));
      }
      throw this.handleAuthError(error);
    }
  }

  /**
   * Sign up with email and password
   */
  async signUp(email: string, password: string): Promise<AuthUser> {
    try {
      const credential: UserCredential = await createUserWithEmailAndPassword(this.auth, email, password);

      // Send email verification
      await sendEmailVerification(credential.user);

      const user = await this.mapFirebaseUserToUser(credential.user);
      return user;
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Sign out
   */
  async logout(): Promise<void> {
    try {
      await signOut(this.auth);
      this.currentUserSubject.next(null);
      this.router.navigate(['/login']);
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Send password reset email
   */
  async sendPasswordReset(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(this.auth, email);
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Resend email verification
   */
  async resendEmailVerification(): Promise<void> {
    const firebaseUser = this.auth.currentUser;
    if (!firebaseUser) {
      throw new Error('No user is currently signed in.');
    }

    try {
      await sendEmailVerification(firebaseUser);
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Update user profile (displayName, photoURL)
   */
  async updateUserProfile(displayName?: string, photoURL?: string): Promise<void> {
    const firebaseUser = this.auth.currentUser;
    if (!firebaseUser) {
      throw new Error('No user is currently signed in.');
    }

    try {
      const updates: { displayName?: string; photoURL?: string } = {};
      if (displayName !== undefined) updates.displayName = displayName;
      if (photoURL !== undefined) updates.photoURL = photoURL;

      await updateProfile(firebaseUser, updates);

      // Update local user state
      const user = await this.mapFirebaseUserToUser(firebaseUser);
      this.currentUserSubject.next(user);
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Verify a password reset oobCode and return the associated email.
   * Used on the reset-password page on init to validate the link before
   * showing the form.
   */
  async verifyPasswordResetCode(oobCode: string): Promise<string> {
    try {
      return await verifyPasswordResetCode(this.auth, oobCode);
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Confirm a password reset using the oobCode from the email link.
   * After this resolves, the user can sign in with the new password.
   */
  async confirmPasswordReset(oobCode: string, newPassword: string): Promise<void> {
    try {
      await confirmPasswordReset(this.auth, oobCode, newPassword);
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Refresh ID token to get updated custom claims
   */
  async refreshToken(): Promise<void> {
    const firebaseUser = this.auth.currentUser;
    if (!firebaseUser) return;

    try {
      await firebaseUser.getIdToken(true); // Force refresh
      const user = await this.mapFirebaseUserToUser(firebaseUser);
      this.currentUserSubject.next(user);
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Get current user (synchronous)
   */
  getCurrentUser(): AuthUser | null {
    return this.currentUserSubject.value;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.currentUserSubject.value !== null;
  }

  /**
   * Check if user has a specific global role (Admin, SuperAdmin, Customer)
   */
  hasRole(role: GlobalRoles): boolean {
    const user = this.getCurrentUser();
    return user ? UserRoleHelper.hasRole(user, role) : false;
  }

  /**
   * Check if user has specific permission
   */
  hasPermission(permission: string): boolean {
    const user = this.getCurrentUser();
    return user ? UserRoleHelper.hasPermission(user, permission) : false;
  }

  /**
   * Handle Firebase auth errors
   */
  private handleAuthError(error: any): Error {
    const message = firebaseErrorMessage(error, error?.message || 'An unknown error occurred.');

    // Preserve the Firebase error code on the thrown Error so callers can
    // branch on `error.code`.
    const wrapped = new Error(message) as Error & { code?: string };
    if (error?.code) {
      wrapped.code = error.code;
    }
    return wrapped;
  }
}
